<template>
  <v-container
  v-if="isDataReady"
  fluid>
    <v-row v-if="$parent.isRole(['SUPERVISOR', 'CLIENT'])" dense no-gutters>
        <v-col>
          <p class="process-repair-tab-section-subtitle text-center">{{$t('pages.repair.partsAreInServiceOptional', {'0': $parent.isRole(['SUPERVISOR']) ? 'the' : 'your'})}}</p>
        </v-col>
    </v-row>
    <v-row dense no-gutters>
        <v-col v-if="$parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SERVICE'])">
          <section-title-with-request-id
          :request-id="repairProp.number"
          title="pages.repair.partsInService"
          class="mb-1"/>
        </v-col>
        <v-col v-else-if="$parent.isRole(['SUPERVISOR', 'CLIENT', 'SUSTAINABILITY_MANAGER'])">
          <section-title-with-request-id
          :request-id="repairProp.number"
          title="pages.repair.partsInfo"
          class="mb-1"/>
        </v-col>
        <v-col
        class="d-flex justify-end align-center"
        cols="1">
          <!-- NOTE: this approval comment is the value set in RepairApproval Tab -->
          <approval-comment-dialog-trigger
          @click="openApprovalCommentDialogForRequest"
          :value="newData.approvalComments['IN_REPAIR_APPROVAL']"
          disable-on-empty/>
        </v-col>
    </v-row>
    <v-form
    ref="form"
    v-model="isFormValid">
      <v-row dense no-gutters>
        <v-col>
          <attachment-dialog
          v-if="attachmentDialog.model"
          v-model="attachmentDialog.model"
          :attachments.sync="attachmentDialog.attachments"
          preview/>
          <approval-comment-dialog
          v-if="approvalCommentDialog.model"
          v-model="approvalCommentDialog.model"
          :comment="approvalCommentDialog.comment"
          :status="approvalCommentDialog.status"
          :step-status="repairProp.status.key"
          :owner-id="approvalCommentDialog.ownerId"
          :saver="approvalCommentDialog.saver"
          preview/>
          <main-data-table
          :headers="getHeaders"
          :items="newData.repairParts"
          number-pages
          :with-search="false"
          :page.sync="page"
          :size="-1"
          :items-per-page-options="[-1]"
          :total-elements="-1"
          hide-default-footer>
            <template v-slot:[`item.attachments`]="{ item }">
              <attachments-dialog-trigger
              @click="openAttachmentsDialog(item.attachments)"
              :attachments="item.attachments"
              disable-on-empty/>
            </template>
            <template v-slot:[`item.imageUrl`]="{ item }">
              <previewable-image
              v-model="item.imageUrl"
              height="65"
              width="50"
              class="py-2"/>
            </template>
            <template v-slot:[`item.repairStatus`]="{ item }">
                <text-field-autocomplete 
                v-if="!disabled && !isDiagnosticStatus(item, ['IT_IS_WORKING']) && $parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SERVICE'])"
                v-model="item.repairStatusObj"
                normal
                itemText="value"
                :label="$t('textFields.selectStatus')"
                :items="repairStatuses"
                :disabled="disabled || item.diagnosticInfo.status === 'IT_IS_WORKING' || item.repairStatus === 'REJECTED'"
                required/>
                <span v-else>
                    {{item.repairStatusObj ? $t(item.repairStatusObj.value) : ''}}
                </span>
            </template>
            <template
            v-slot:[`item.approvalComments`]="{ item }">
              <!-- NOTE: this approval comment is the value set in RepairApproval Tab -->
              <approval-comment-dialog-trigger
              @click="openApprovalCommentDialogForPart(item)"
              :value="item.approvalComments['IN_REPAIR_APPROVAL']"
              disable-on-empty/>
            </template>
            <template 
            v-if="$parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SERVICE'])"
            v-slot:[`item.repairComment`]="{ item }">
                <div class="py-2">
                  <text-area
                  v-if="!disabled && !isDiagnosticStatus(item, ['IT_IS_WORKING']) && isRepairApprovalStatus(item, ['APPROVED'])"
                  v-model="item.repairComment"
                  solo-normal
                  :disabled="item.diagnosticInfo.status === 'IT_IS_WORKING'"
                  no-resize/> 
                  <span v-else>
                    {{item.repairComment}}
                  </span>
                </div>
            </template>
            <template 
            v-if="$parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SERVICE'])"
            v-slot:[`item.action`]="{ item }">
                <div 
                v-if="isRepairApprovalStatus(item, ['APPROVED']) && !isDiagnosticStatus(item, ['IT_IS_WORKING'])"
                class="d-flex justify-start">  
                  <primary-btn
                  @click="setChangedDialogItemsBySave(item)"
                  :loading="isLoading"
                  :label="$t('btns.save')"
                  color="#4285f4"
                  class="mx-2"
                  :disabled="disabled || !item.repairStatusObj"
                  small-text
                  :width="50"
                  :height="30"/>
                </div>
            </template>
          </main-data-table>
        </v-col>
      </v-row>
    </v-form>
    <v-row 
    v-if="$parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SERVICE'])"
    dense class="mt-5">
      <v-col
      cols="11">
        <p class="text-center"
        style="font-weight: bold">
          {{$t('common.pressContinueWhenReady')}}
        </p>
        <process-repair-quick-btns
        :loading.sync="isLoading"
        :config="getQuickBtnsConfig"
        on-success-repair-status-message="dialogs.messages.success.toTransitBackStep"/>
      </v-col>
      <v-col
      cols="1">
        <primary-btn
        @click="setChangedDialogItemsBySaveAll"
        :loading="isLoading"
        :width="120"
        :height="50"
        color="#4285f4"
        :disabled="disabled || !isFormValid"
        :label="$t('btns.saveAll')"/>
      </v-col>
    </v-row>

    <export-xlsx-row
    :width="150"
    :height="50"
    :disabled="newData.repairParts.length === 0"
    :loading.sync="isLoading"
    class="px-4 mt-4"
    name="export.title.repairTab"
    :exporter="getExporter"/>
  </v-container>
</template>
<script>
import TextArea from '@/components/text-fields/TextArea'
import DateUtil from '@/utils/DateUtil'
import MainDataTable from '@/components/common/MainDataTable'
import PreviewableImage from '@/components/common/PreviewableImage'
import PrimaryBtn from '@/components/btns/PrimaryBtn'
import ExportXlsxRow from '@/components/common/ExportXlsxRow'
import ProcessRepairQuickBtns from '@/components/process-repair-tabs/ProcessRepairQuickBtns'
import AttachmentDialog from '@/components/dialogs/AttachmentDialog'
import AttachmentsDialogTrigger from '@/components/common/triggers/AttachmentsDialogTrigger'
import SectionTitleWithRequestId from '@/components/common/process-tabs/shared/SectionTitleWithRequestId'
import ApprovalCommentDialog from '@/components/dialogs/ApprovalCommentDialog'
import ApprovalCommentDialogTrigger from '@/components/common/triggers/ApprovalCommentDialogTrigger'
import { getAvailableServiceStatuses, getObjectServiceStatusByKey, getServiceStatusByKey } from '@/utils/statusesUtil'
import { updateAllPartsServiceData } from '@/repositories/repairRepo'
import TextFieldAutocomplete from '@/components/text-fields/TextFieldAutocomplete'
import { execHttpRequestThrowable } from '@/services/http'
//ENABLE_CHANGE_DIALOG - This method is needed for changes dialog.
// compareArrays
import { cloneObj, copyObj } from '@/utils/StringUtil'
import { exportRepairTabXlsx } from '@/repositories/exportRepo'

export default {
  components: {
    ProcessRepairQuickBtns,
    TextArea,
    TextFieldAutocomplete,
    MainDataTable,
    PreviewableImage,
    PrimaryBtn,
    ExportXlsxRow,
    AttachmentDialog,
    AttachmentsDialogTrigger,
    SectionTitleWithRequestId,
    ApprovalCommentDialog,
    ApprovalCommentDialogTrigger,
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    disabled: Boolean
  },
  data() {
    return {
      isFormValid: false,
      headers: [
        {
          text: 'headers.image',
          align: 'start',
          sortable: true,
          value: 'imageUrl'
        },{
          text: 'headers.category',
          align: 'start',
          sortable: true,
          value: 'category'
        },{
          text: 'headers.partName',
          align: 'start',
          sortable: true,
          value: 'partName'
        }, {
          text: 'headers.serialNumber',
          align: 'start',
          sortable: true,
          value: 'serialNumber'
        }, {
          text: 'headers.serviceTag',
          align: 'start',
          sortable: true,
          value: 'serviceTag'
        },{
          text: 'headers.repairStatus',
          align: 'start',
          sortable: true,
          value: 'repairStatus'
        }, {
          text: 'headers.approvalComment',
          align: 'start',
          sortable: true,
          value: 'approvalComments'
        }, {
          text: 'headers.repairComment',
          align: 'start',
          sortable: true,
          value: 'repairComment'
        }, {
          text: 'headers.attachments',
          align: 'start',
          sortable: true,
          value: 'attachments'
        }, {
          text: 'headers.action',
          align: 'start',
          sortable: true,
          value: 'action'
        }
      ],
      repairStatuses: getAvailableServiceStatuses(),
      page: 1,
      isLoading: false,
      newData: null,
      isDataReady: false,
      attachmentDialog: {
        model: false
      },
      approvalCommentDialog: {
        model: false,
        comment: null,
        status: null,
        ownerId: -1,
        isItem: false,
        saver: null
      },
    }
  },
  computed: {
    getHeaders() {
      const headers = []
      const ignoreColumsForSupervisor = ["repairComment", "action"]
      const ignoreColumsForSM = ["action"]
      const ignoreColumsForClient = ["action"]

      for (const h of this.headers) {
        if (this.$parent.isRole(["SUPERVISOR"])) {
          if (ignoreColumsForSupervisor.includes(h.value)) continue
        }
        else if (this.$parent.isRole(["SUSTAINABILITY_MANAGER"])) {
          if (ignoreColumsForSM.includes(h.value)) continue
        }
        else if (this.$parent.isRole(["CLIENT"])) {
          if (ignoreColumsForClient.includes(h.value)) continue
        }
        headers.push(h)
      }

      return headers
    },
    repairProp: {
      get: function() {
        return this.$props.data
      },
      set: function(v) {
        this.$emit('update:data', v)
      }
    },
    getQuickBtnsConfig() {
      return [
        {
          name: 'AllInProgress',
          events: {
            'click': () => this.setChangedDialogItemsByStatus('IN_PROGRESS')
          },
          props: {
            'disabled': this.disabled
          }
        }, {
          name: 'AllReplaced',
          events: {
            'click': () => this.setChangedDialogItemsByStatus('REPLACED')
          },
          props: {
            'disabled': this.disabled
          }
        }, {
          name: 'AllRepaired',
          events: {
            'click': () => this.setChangedDialogItemsByStatus('REPAIRED')
          },
          props: {
            'disabled': this.disabled
          }
        }, {
          name: 'AllNonRepairable',
          events: {
            'click': () => this.setChangedDialogItemsByStatus('NON_REPAIRABLE')
          },
          props: {
            'disabled': this.disabled
          }
        }, {
          name: 'Continue',
          props: {
            'disabled': this.disabled || !this.newData.isAllowedToContinue
          }
        }
      ]
    },
    getExporter() {
      return async () => await exportRepairTabXlsx(this.$route.params.id)
    }
  },
  methods: {
    async tryToUpdateAllPartsServiceData(parts) {
      const data = parts.map(p => {
        return {
          repairPartId: p.id,
          status: p.repairStatusObj.key,
          comment: p.repairComment
        }
      })
      const messages = {
        _200: this.$t(parts.length > 0 ? 'dialogs.messages.success.repairStatusesSavedSuccessfully' : 'dialogs.messages.success.repairStatusSavedSuccessfully')
      }
      const r = await execHttpRequestThrowable(async () => await updateAllPartsServiceData(this.$route.params.id, data),
      messages,
      v => this.isLoading = v)
      const ids = parts.map(p => p.id)
      //Update server parts
      this.repairProp.repairParts
        .filter(p => ids.includes(p.id))
        .forEach((p, i) => {
          cloneObj(parts[i], p)
        })
      this.newData.repairParts
        .filter(p => ids.includes(p.id))
        .forEach((p, i) => {
          cloneObj(parts[i], p)
        })

      this.newData.isAllowedToContinue = r.response.data
    },
    openAttachmentsDialog(attachments) {
      this.attachmentDialog = {
        model: true,
        attachments
      }
    },
    openApprovalCommentDialogForPart({approvalComments, initialApproval, id}) {
      // NOTE: this approval comment is the value set in RepairApproval Tab
      this.approvalCommentDialog = {
        model: true,
        comment: approvalComments["IN_REPAIR_APPROVAL"],
        status: initialApproval,
        ownerId: id,
        isItem: true,
        saver: null
      }
    },
    openApprovalCommentDialogForRequest() {
      // NOTE: this approval comment is the value set in RepairApproval Tab
      this.approvalCommentDialog = {
        model: true,
        comment: this.newData.approvalComments["IN_REPAIR_APPROVAL"],
        status: "REQUEST",
        ownerId: Number(this.$route.params.id),
        isItem: false,
        saver: null
      }
    },
    setChangedDialogItemsByStatus(s) {
      const filteredParts = this.newData.repairParts.filter(rp => rp.diagnosticInfo.status !== 'IT_IS_WORKING' && rp.repairStatus !== 'REJECTED')
      const newParts = filteredParts.map(p => {
        const newObject = copyObj(p)
        newObject.repairStatusObj = getObjectServiceStatusByKey(s)
        return newObject
      })
      const ids = newParts.map(p => p.id)
      const oldParts = this.repairProp.repairParts.filter(rp => ids.includes(rp.id))

      this.setChangedDialogItems(oldParts, newParts, async () => await this.tryToUpdateAllPartsServiceData(newParts))
    },
    setChangedDialogItemsBySave(p) {
      const old = this.repairProp.repairParts.filter(rp => rp.id === p.id)[0]
      const _new = p
      this.setChangedDialogItems([old], [_new], async () => await this.tryToUpdateAllPartsServiceData([_new]))
    },
    setChangedDialogItemsBySaveAll() {
      const newParts = this.newData.repairParts.filter(rp => rp.diagnosticInfo.status !== 'IT_IS_WORKING' && rp.repairStatus !== 'REJECTED')
      const ids = newParts.map(p => p.id)
      const oldParts = this.repairProp.repairParts.filter(rp => ids.includes(rp.id))
      this.setChangedDialogItems(oldParts, newParts, async () => await this.tryToUpdateAllPartsServiceData(newParts))
    },
    setChangedDialogItems(oldParts, newParts, action) {
      action()
      // NOTE: ENABLE_CHANGE_DIALOG - Enable this code, if changes dialog is needed.
      //-----------------------------------------------------------------
      // const props = [
      //   {
      //     field: 'headers.repairStatus',
      //     prop: 'repairStatusObj.key',
      //     getter: getServiceStatusByKey
      //   }, {
      //     field: 'headers.repairComment',
      //     prop: 'repairComment'
      //   }
      // ]

      // this.$changesDialog.open(
      //   'dialogs.titles.changes',
      //   compareArrays(oldParts, newParts, props, false),
      //   action,
      //   true
      // )
      //-----------------------------------------------------------------
    },
    getDateNewYorkFormat(date) {
      return DateUtil.getDateNewYorkFormat(date)
    },
    getRepairStatus(k) {
      return getServiceStatusByKey(k)
    },
    isDiagnosticStatus(i, s) {
      return s.includes(i.diagnosticInfo.status)
    },
    isInitialStatus(i, s) {
      return s.includes(i.initialApproval)
    },
    isRepairApprovalStatus(i, s) {
      return s.includes(i.repairApproval)
    }
  },
  mounted() {
    this.newData = {
      isAllowedToContinue: this.repairProp.isAllowedToContinue,
      approvalComments: copyObj(this.repairProp.approvalComments),
      repairParts: this.repairProp.repairParts
        .filter(rp => rp.initialApproval === 'APPROVED')
        .map((rp, i) => {
          rp.repairStatusObj = getObjectServiceStatusByKey(rp.repairStatus)
          rp.rowNumber = i + 1
          return copyObj(rp)
        })
    }
    this.isDataReady = true
  }
}
</script>
<style>

</style>